import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  Link,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * Privacy Policy page component
 * 
 * Contains the full privacy policy for the MyEventShop platform
 * detailing how user data is collected, processed, stored and protected
 */
const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };
  
  // Last Updated date
  const lastUpdated = "21st November 2025";

  // Table of contents items
  const tableOfContents = [
    { title: '1. Introduction', id: 'introduction' },
    { title: '2. What is personal data?', id: 'personal-data' },
    { title: '3. The types of Data we process', id: 'data-types' },
    { title: '4. Safety of Minors', id: 'minors' },
    { title: '5. How we collect personal information', id: 'collection' },
    { title: '6. How we use personal information', id: 'usage' },
    { title: '7. When we disclose personal data', id: 'disclosure' },
    { title: '8. International Transfers', id: 'international-transfers' },
    { title: '9. Security', id: 'security' },
    { title: '10. Retention period', id: 'retention' },
    { title: '11. Accessing, correcting, or downloading your personal information', id: 'access' },
    { title: '12. Exercising your other rights', id: 'rights' },
    { title: '13. Conditions and changes to this Privacy Policy', id: 'changes' },
    { title: '14. To contact our Privacy Officer', id: 'contact' }
  ];

  return (
    <Box>
      <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Privacy Policy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Privacy Policy Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Box id="introduction" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Introduction
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                About this statement and how to read it
              </Typography>
              <Typography variant="body1" paragraph>
                We are happy to welcome you to our website. The terms "We", "Us"," Our" means MyEventShop. The terms "You" and "Your" refer to You, as a user of our products, services, and customer support or visitor of our website/ application/ services.
              </Typography>
              <Typography variant="body1" paragraph>
                This Privacy Notice ("Notice") describes how we processes Personal Data in our capacity as a controller (i.e., when we determine the purposes and means of the processing of personal data). It also describes your choices and rights regarding your Personal Data which has been mentioned below in detail.
              </Typography>
              <Typography variant="body1" paragraph>
                We may also process your personal data as a processor (i.e. when data is processed under the instructions of a controller). In that situation, this notice does not apply to you. It is advised to refer to the controller's privacy notice for the purpose of transparency.
              </Typography>
              <Typography variant="body1" paragraph>
                The said policy has been created in compliance to the EU Data Protection Legislation which comprises- The General Data Protection Regulation (No 2016/679/EU), Privacy and Electronic Communications Directive 2002/58/EC and other relevant laws as will be updated when required.
              </Typography>
              <Typography variant="body1" paragraph>
                This Privacy Policy applies to personal data collected and/or held by us and does not apply to any third-party websites that may be linked to our websites, which will be governed by their own respective privacy policies.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="personal-data" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. What is personal data?
              </Typography>
              <Typography variant="body1" paragraph>
                Personal data is any data that relates to an identifiable or identified individual. GDPR covers a broad spectrum of information that could be used on its own, or in combination with other pieces of information, to identify a person. An identifiable natural person is one who can be identified directly or indirectly, in particular, by reference to an identifier such as a name, an identification number, location data, an online identifier or one or more specific characteristics expressing the physical, physiological, genetic, psychological, economic, cultural or social identity of that natural person.
              </Typography>
              <Typography variant="body1" paragraph>
                Personal data extends beyond a person's name or email address. Some examples include name, gender, date of birth, address, feedback, biometric data, IP addresses, ethnicity etc.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="data-types" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. The types of Data we process
              </Typography>
              <Typography variant="body1" paragraph>
                We collect personal data of our users in order to provide our services to the best of our ability. Our services are provided through many platforms including but not limited to: websites, mobile applications, email, and telephone. The specific platform and service, or support you interact with may involve the personal data we process.
              </Typography>
              <Typography variant="body1" paragraph>
                In some situations, users may provide us with personal data without us asking for it, or through means not intended for the collection of particular types of information. While we take reasonable steps to protect this data, the user will have bypassed our systems, processes, and control and thus the personal data provided will not be governed by this privacy policy.
              </Typography>
              <Typography variant="body1" paragraph>
                In some situations, users may provide us personal data over platforms that are outside our control; for example through social media or forums. Whilst any personal data processed by us is governed by this Privacy Notice, the platform by which it was communicated will be governed by its own Privacy Policy or by any arrangement between us and the respective platform.
              </Typography>
              <Typography variant="body1" paragraph>
                Not all information requested, collected, and processed by us is "Personal Data" as it does not identify you as a specific natural person. This will include the majority of "User Generated Content" that you provide us with the intention of sharing with other users. Such "Non-Personal Data" is not covered by this Privacy Notice. However, as non-personal information may be used in aggregate or be linked with existing personal data; when in this form it will be treated as personal data. As such, this privacy policy has listed both types of information for the sake of transparency.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="minors" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. Safety of Minors
              </Typography>
              <Typography variant="body1" paragraph>
                Our products/services are not intended for and may not be used by minors. We do not knowingly collect personal data from Minors or allow them to register. If it comes to our attention that we have collected personal data from a Minor, we may delete this information without notice. If you have reason to believe that this has occurred, please contact us at admin@myeventshop.com.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="collection" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. How we collect personal information
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                Personal data that you specifically give us
              </Typography>
              <Typography variant="body1" paragraph>
                While you use our products and services you may be asked to provide certain types of personal data. This might happen through our website and platform. We may process the following personal data:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Identity Details</strong> - First Name and Last Name</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Account Details</strong> - Username and Password</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Contact Details</strong> - Email</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                There can be some "User Generated Content" that you provide to us for which you do not have the intention of sharing your personal identifiable details with other users.
              </Typography>
              <Typography variant="body1" paragraph>
                In cases where such data is collated and used for reporting and analytical purposes, such data shall not carry personal identifiable details such as username, phone number, email and physical address thereby not disclosing the personal data to other users.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                Personal data we collect as you use our service
              </Typography>
              <Typography variant="body1" paragraph>
                We maintain records of the interactions we have with our users, including the products, services and customer support we have provided. This includes the interactions our users have with our platform such as when a user has viewed a page or clicked a button.
              </Typography>
              <Typography variant="body1" paragraph>
                When we are contacted, we may collect personal data that is intrinsic to the communication. For example, if we are contacted via email, we will collect the email address used.
              </Typography>
              <Typography variant="body1" paragraph>
                We may collect or process the following:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Metadata</strong> - IP address, computer and connection information, referring web page, standard web log information, language settings, time zone, etc.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Device Information</strong> - device identifier, device type, device plugins, hardware capabilities, etc.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Actions</strong> - pages viewed, buttons clicked, time spent viewing, search keywords, etc.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Location Details</strong> - Geolocation as a part of our service to provide details of events near you</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                We may send cookies to your computer in order to uniquely identify your browser and improve the quality of our service. The term "cookies" refers to small pieces of information that a website sends to your computer's hard drive while you are viewing the site. We may use both session cookies (which expire once you close your browser) and persistent cookies (which stay on your computer until you delete them). Persistent cookies can be removed by following your browser help directions.
              </Typography>
              <Typography variant="body1" paragraph>
                The usage information we collect helps us to improve the Website and our services by enabling us to:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Store information about your preferences, allowing us to customize the website and our services.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Recognize you when you use the website.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Use location information to track loads and other transactions through the applications.</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                All of the above shall be done on the basis of your consent.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                Links to other sites
              </Typography>
              <Typography variant="body1" paragraph>
                On our website, you will encounter links to third party websites. These links may be from us, or they may appear as content generated by other users. These linked sites are not under our control and thus we are not responsible for their actions. Before providing your personal information via any other website, we advise you to examine the terms and conditions of using that website and its privacy policy.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="usage" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. How we use personal information
              </Typography>
              <Typography variant="body1" paragraph>
                The information we process is primarily used to provide users with the product or service they have requested. More specifically, we may use your personal information for the following purposes:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to provide the service or product you have requested</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to facilitate the creation of a User Account</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to provide technical or other support to you</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to answer enquiries about our services, or to respond to a complaint</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to allow for debugging, testing and otherwise operate our platforms</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">to comply with legal and regulatory obligations</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">if otherwise permitted or required by law; or</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">for other purposes with your consent, unless you withdraw your consent for these purposes</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                The 'lawful processing' grounds on which we will use personal information about our users are (but are not limited to):
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">when a user has given consent</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">processing is necessary for compliance with our legal obligations</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">processing is necessary in order to protect the vital interests of our users or of another natural person</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">processing is necessary for the performance of a contract to which you may be a party or in order to take steps at the user's request prior to entering into a contract</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">processing is done in pursuing our legitimate interests, where these interests do not infringe on the rights of our users</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">processing is necessary for the performance of a task carried out in the public interest</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="disclosure" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. When we disclose personal data
              </Typography>
              <Typography variant="body1" paragraph>
                The personal information of users may be held, transmitted to or processed on our behalf, by our third-party service providers. Our third-party service providers are bound by contract to only use your personal data on our behalf, under our instructions.
              </Typography>
              <Typography variant="body1" paragraph>
                The following table describes the services and purpose for which these processors have been engaged.
              </Typography>
              
              <Box sx={{ overflowX: 'auto', my: 3 }}>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>VENDOR</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>PURPOSE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>DATA CENTERS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      <TableCell>Amazon Web Services, Inc.</TableCell>
                      <TableCell>Primary cloud infrastructure provider for YouFactors, where all SaaS applications are hosted. Almost all data stored, processed and transmitted through YouFactors products and services resides on Amazon Web Services data centres.</TableCell>
                      <TableCell>EU</TableCell>
                    </TableRow>
                    <TableRow sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      <TableCell>Brevo</TableCell>
                      <TableCell>Cloud-based software company solution for marketing and events marketing</TableCell>
                      <TableCell>EU</TableCell>
                    </TableRow>
                    <TableRow sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      <TableCell>Stripe</TableCell>
                      <TableCell>Payment-processing software and application programming interfaces for our services</TableCell>
                      <TableCell>EU</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                Other disclosures and transfers
              </Typography>
              <Typography variant="body1" paragraph>
                We may also disclose your personal information to third parties for the following purposes:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">if necessary, to provide the service or product you have requested</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">requests for information by law enforcement</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">if otherwise permitted or required by law; or</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">for other purposes with your consent</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="international-transfers" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. International Transfers Outside of the European Economic Area (EEA)
              </Typography>
              <Typography variant="body1" paragraph>
                We will ensure that any transfer of personal information from countries in the European Economic Area (EEA) to countries outside the EEA will be protected by appropriate safeguards under applicable law governing such transfers, which may include. We take steps to minimise transfers of your personal information outside of the EEA, however, there may be some circumstances where we or our service providers do transfer your personal information to a destination outside the EEA.
              </Typography>
              <Typography variant="body1" paragraph>
                We will only transfer your data in compliance with data protection laws and provided appropriate safeguards to protect personal information being transferred are in place, such as an adequacy decision, Data Protection Framework, approved model contractual clauses, or binding corporate rules.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="security" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                9. Security
              </Typography>
              <Typography variant="body1" paragraph>
                Appropriate security measures are implemented in order to protect your personal data. Security measures refer to physical security in the office (e.g., securely locked filing cabinets etc.) as well as implementing appropriate technology and cyber security measures across our systems and networks in order to prevent any accidental or unauthorised access, interference, damage, loss or disclosure of personal data. In the event of certain types of personal data breaches, we are legally obliged to notify the Supervisory Authority and affected individuals to whom the personal data belong. We have implemented internal procedures to manage personal data security breaches in accordance with our legal obligations.
              </Typography>
              <Typography variant="body1" paragraph>
                We use up to date appropriate encryption techniques that your systems support when transmitting data via our systems. However, we cannot guarantee that all internet or email transmission is fully secure or error free and except for our guarantee to use commercially reasonable and up to date measures to technically secure any data transmission. We cannot guarantee their absolute security and we therefore cannot be held liable for intercepted information sent via the internet or for third parties using revoked, stolen, forged, or otherwise insecure certificates. You should therefore take due care in deciding what information you want to share with us.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="retention" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                10. Retention period
              </Typography>
              <Typography variant="body1" paragraph>
                We store your personal data for as long as it is necessary for the purpose for which it was collected or for fulfilling our legal obligations, such as statutory retention periods. Where we no longer need to process your personal data for the purposes set out in this Privacy Policy, we will delete your personal data from our systems unless we need to retain a limited amount of information to make sure that we act in accordance with your wishes.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="access" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                11. Accessing, correcting, or downloading your personal information
              </Typography>
              <Typography variant="body1" paragraph>
                You have the right to request access to the personal data about you. Unless an exception applies, we must allow you to access the personal data we hold about you, within a reasonable time period, and without unreasonable expense for no charge. Most personal information can be accessed by logging into your account. If you wish to access information that is not accessible through the platform or wish to download all personal information, we hold on you in a portable data format, please contact us at admin@myeventshop.com.
              </Typography>
              <Typography variant="body1" paragraph>
                You also have the right to request the correction of the personal information we hold about you. All your personal information can be updated through the user settings pages. If you require assistance, please contact us at admin@myeventshop.com.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="rights" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                12. Exercising your other rights
              </Typography>
              <Typography variant="body1" paragraph>
                You have a number of rights in respect to your personal data. These are:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>i.</strong> The right to access your personal data, which includes receiving confirmation on whether the personal data are being processed and if so, receiving the personal data and related information about why they are being processed, the categories of personal data involved, to whom the personal data have been or will be shared and how long the data will be kept for. We will accede to any such valid requests within one month of the receipt of a valid request</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>ii.</strong> The right to request that we rectify inaccurate data or update incomplete data. You may also request that we restrict the processing of the personal data until the rectification or updating has been completed, although please be aware that we may have to suspend the operation of your account or the products or services that we provide.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>iii.</strong> The right to request that we erase your data under certain circumstances, including where you want to withdraw the consent you previously gave to us, where you object to the processing of the data for its own legitimate interests or where processing of the data is unlawful. In the case of unlawful processing, you can also request that this processing is restricted rather than the personal data being erased. Please be aware that we may have to suspend the operation of your account or the products or services that we provide where data processing is restricted.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>iv.</strong> The right to object to the processing of your personal data, where such processing is being conducted for the purpose of:</Typography>
                  <List sx={{ pl: 3, listStyleType: 'disc', mt: 1 }}>
                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <Typography variant="body1"><strong>A.</strong> Direct marketing;</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <Typography variant="body1"><strong>B.</strong> Establishing, exercising or defending ourselves or others from legal claims; or</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <Typography variant="body1"><strong>C.</strong> Our legitimate interests, unless we can demonstrate that our interests override your interests and rights. You may request that we restrict the processing of the personal data until this analysis of legitimate interests has been concluded, although please be aware that we may have to suspend the operation of your account or the products or services that we provide where data processing is restricted.</Typography>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>v.</strong> The right to receive your data in a portable format or, subject to it being technically feasible, have us transfer it directly to a third party. This applies where you have provided us with consent for the processing or where the processing is necessary for entering a contract with us.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>vi.</strong> The right, at any time, to withdraw consent you have provided to us to process your personal data.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>vii.</strong> The right to lodge a complaint to the Data Protection Commission or another supervisory authority. The Office of the Data Protection Commission can be contacted at:</Typography>
                  <Typography variant="body1" sx={{ mt: 1, pl: 2 }}>
                    Email: info@dataprotection.ie<br />
                    Telephone: +353 (0)761 104 800<br />
                    Postal Address: Data Protection Commission, 21 Fitzwilliam Square South, Dublin 2, D02 RD28
                  </Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                If you wish to raise a complaint in relation to how we processed your personal data, please contact us. We take your privacy and data protection very sincerely and we endeavour to address your complaint as expediently and as thoroughly as we can in order to find a satisfactory resolution for you.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="changes" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                13. Conditions and changes to this Privacy Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We will update this Privacy Policy from time to time. Any changes will be made available on our website and, where appropriate, notified to you by written notice or e-mail. These changes will be effective immediately for new users of our services and will become effective for existing users through continued use of our services after the effective date of the posted change. If you do not wish to approve the changes to our use of your personal information, you must notify us before such changes take effect that you wish to deactivate your account with us. Please note that you are always responsible for keeping your personal information up to date and providing us with your current contact information.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                14. To contact our Privacy Officer
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about our privacy statement, your rights, or how we use your information, please do not hesitate to contact our Privacy Office, at:
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop<br />
                436, Wyckham Point,<br />
                Wyckham Way, Dundrum,<br />
                Dublin, Ireland<br /><br />
                Email: admin@myeventshop.com
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Call to Action */}
        <Paper 
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            mb: 8,
            bgcolor: 'rgba(95, 60, 111, 0.05)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="p" mb={3}>
            Have questions about our Privacy Policy? We're here to help.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 'medium',
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default PrivacyPolicy; 